import React, {
  SyntheticEvent,
  CSSProperties,
  useCallback,
  useRef,
  useState,
} from 'react';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import Autocomplete, {
  AutocompleteRenderInputParams,
  createFilterOptions,
} from '@mui/material/Autocomplete';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { Box, Button, Chip, Paper } from '@mui/material';
import Cancel from '@mui/icons-material/Cancel';

import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;
type SelectOption = {
  text: string;
  secondaryText?: string;
  value: string;
  hasWarning?: boolean;
  isArchived?: boolean;
  isDisabled?: boolean;
  isHidden?: boolean;
  status?: string;
};

const getOptionDisplayText = (option: SelectOption) =>
  option.secondaryText ? `${option.text} ${option.secondaryText}` : option.text;

const renderOptionText = (option: SelectOption) => (
  <>
    {option.text}
    {option.secondaryText && (
      <>
        {'\u00A0'}
        <Box component="span" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
          {option.secondaryText}
        </Box>
      </>
    )}
  </>
);

type SelectWithSearchProps = {
  label: string;
  placeholder: string;
  disabled?: boolean;
  disableClearable?: boolean;
  showArchived?: boolean;
  showResultsUnderSearchTextBox?: boolean;
  hideStatusIcon?: boolean;
  size?: 'small' | 'medium';
  error?: boolean;
  helperText?: React.ReactNode;
  isValidNewItem?: (input: string) => boolean;
  options: SelectOption[];
  values: string[];
  handleAddItem?: (text: string) => void;
  handleSelectChange: (
    event: SyntheticEvent<Element, Event> | undefined,
    value: SelectOption[],
  ) => void;
  entity?: string;
  style?: CSSProperties;
};

function SelectWithSearch({
  label,
  placeholder,
  options,
  values,
  handleSelectChange,
  style,
  handleAddItem,
  disabled,
  disableClearable,
  showResultsUnderSearchTextBox,
  hideStatusIcon,
  isValidNewItem,
  entity,
  size,
  error,
  helperText,
}: SelectWithSearchProps) {
  const [inputValue, setInputValue] = useState('');
  const filter = createFilterOptions<SelectOption>();
  const selectedValues = React.useMemo(
    () => options.filter(v => values.includes(v.value)),
    [values, options],
  );
  const shouldShowAddItemButton =
    !!handleAddItem &&
    !!inputValue &&
    (isValidNewItem ? isValidNewItem(inputValue) : true) &&
    !options.some(v => v.text === inputValue);
  const paperStateRef = useRef<{
    shouldShowAddItemButton: boolean;
    inputValue: string;
    entity?: string;
  }>({
    shouldShowAddItemButton: false,
    inputValue: '',
    entity: undefined,
  });
  const handleAddItemRef = useRef(handleAddItem);
  const highlightedOptionRef = useRef<SelectOption | null>(null);
  // On open MUI highlights the first selected option and scrolls the list to it, which for a
  // multi-select lands mid-list. Runs right after MUI's own listbox ref, so the list opens at the top.
  // Must stay stable: a new ref each render would scroll back to the top on every selection.
  const scrollListboxToTop = useCallback((listbox: Element | null) => {
    if (listbox) listbox.scrollTop = 0;
  }, []);

  paperStateRef.current = {
    shouldShowAddItemButton,
    inputValue,
    entity,
  };
  handleAddItemRef.current = handleAddItem;

  const renderPaper = useCallback((props: React.HTMLAttributes<HTMLElement>) => {
    const { children, ...rest } = props;
    const state = paperStateRef.current;
    const isContact = state.entity === 'Contact';

    return (
      <Paper {...rest}>
        {children}
        {state.shouldShowAddItemButton && (
          <Button
            sx={{
              marginLeft: isContact ? '5px' : '25px',
              marginBottom: isContact ? '5px' : '25px',
              marginTop: isContact ? '-5px' : '25px',
            }}
            variant="contained"
            onMouseDown={() => {
              handleAddItemRef.current?.(state.inputValue);
            }}
          >
            Add New {state.entity ? state.entity : 'Item'}
          </Button>
        )}
      </Paper>
    );
  }, []);

  const getStatusIcon = (status: string | undefined) => {
    if (!status) {
      return <RadioButtonUncheckedIcon htmlColor="grey" />;
    }
    switch (status) {
      case 'unconfirmed':
        return <RadioButtonUncheckedIcon htmlColor="grey" />;
      case 'accepted':
        return <CheckCircleOutlineIcon style={{ fill: 'green' }} />;
      case 'declined':
        return <HighlightOffIcon style={{ fill: 'red' }} />;
    }
  };

  return (
    <Autocomplete<SelectOption, true, boolean, false>
      multiple
      size={size}
      style={style}
      options={options}
      disabled={!!disabled}
      disableClearable={disableClearable}
      disableCloseOnSelect
      getOptionKey={(option: SelectOption) => option.value}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      noOptionsText={'No options'}
      getOptionDisabled={(option: SelectOption) => {
        return !!option.isDisabled;
      }}
      getOptionLabel={(option: SelectOption) => {
        const displayText = getOptionDisplayText(option);
        if (option.isArchived) {
          return `${displayText} (Archived)`;
        } else {
          return option.hasWarning ? `${displayText} ⚠` : displayText;
        }
      }}
      value={selectedValues}
      filterOptions={(options, params) => filter(options, params)}
      onInputChange={(_, value) => {
        highlightedOptionRef.current = null;
        setInputValue(value);
      }}
      onHighlightChange={(_, option) => {
        highlightedOptionRef.current = option;
      }}
      onKeyDown={event => {
        if (
          event.key === 'Enter' &&
          shouldShowAddItemButton &&
          !highlightedOptionRef.current
        ) {
          event.preventDefault();
          handleAddItem?.(inputValue);
        }
      }}
      renderTags={(tagValue, getTagProps) => {
        if (showResultsUnderSearchTextBox) {
          return;
        }
        const tags = tagValue.map(
          (
            option: SelectOption,
            index,
          ) => (
            // eslint-disable-next-line react/jsx-key
            <Chip
              deleteIcon={option.isArchived || option.isDisabled ? <></> : <Cancel />}
              {...getTagProps({ index })}
              label={
                <>
                  {renderOptionText(option)}
                  {option.isArchived ? ' (Archived)' : option.isDisabled ? ' (Dynamic)' : ''}
                </>
              }
            />
          ),
        );
        return <Box>{tags}</Box>;
      }}
      renderOption={(
        props,
        option: SelectOption,
        { selected },
      ) => {
        if (option.isArchived || option.isHidden) {
          return null;
        }
        return (
          <li {...props}>
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              style={{ marginRight: 8 }}
              disabled={option.isDisabled || option.isArchived}
              checked={selected}
            />
            {renderOptionText(option)}
          </li>
        );
      }}
      PaperComponent={renderPaper}
      ListboxProps={{ ref: scrollListboxToTop }}
      onChange={handleSelectChange}
      renderInput={(params: AutocompleteRenderInputParams) => {
        return (
          <>
            <TextField
              {...params}
              label={label}
              placeholder={placeholder}
              size={size}
              error={error}
              helperText={helperText}
            />
            {showResultsUnderSearchTextBox && (
              <Box sx={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                {selectedValues.map(option => (
                  // eslint-disable-next-line react/jsx-key
                  <Chip
                    icon={hideStatusIcon ? <></> : getStatusIcon(option.status)}
                    sx={{
                      marginBottom: '5px',
                      textAlign: 'left',
                      '&.MuiChip-root': {
                        display: 'flex',
                        justifyContent: 'space-between',
                      },
                      '& .MuiChip-label': {
                        flex: 1,
                      },
                    }}
                    deleteIcon={option.isArchived || option.isDisabled ? <></> : <Cancel />}
                    onDelete={() => {
                      const nextSelectedValues = selectedValues.filter(
                        s => s.value !== option.value,
                      );
                      handleSelectChange(undefined, nextSelectedValues);
                    }}
                    label={
                      <>
                        {renderOptionText(option)}
                        {option.isArchived ? ' (Archived)' : option.isDisabled ? ' (Dynamic)' : ''}
                      </>
                    }
                  />
                ))}
              </Box>
            )}
          </>
        );
      }}
    />
  );
}

export default SelectWithSearch;
